import { getConfigForImportedTest, getConfigForMeasuredTest } from 'Components/Worksheets/Config';
import {
  wrapAsDerivedFromMeasurementsConfig as derivedFrom,
  measuredUsingConfigFor as measuredUsing,
  mergeConfig,
} from 'Components/Worksheets/Core/Helpers/configHelpers';
import { GrainSize } from 'Components/Worksheets/MoistureContentWorksheet/types';
import { FormatTypes } from 'Lib/Format';
import externalTestMethods from '../../../ExternalTestMethods';
import {
  getMoistureContentPercent,
  getConfig as mcCOnfig,
} from '../../../MoistureContentWorksheet/BsEnIso/Config/index';
import qestIds from '../../../QestIds';
import { getDerivedFromMeasurementsConfigForPortionMassMeasurement } from '../../derivedFromMeasurementsConfig';
import { getMeasuredUsingConfigForPortionMassMeasurement } from '../../measuredUsingConfig';
import getLabelConfig from './labelConfig';

// default when it cannot be determined by balance readability etc.
const massConfig = { uom: 'g', format: { nearest: 0.01 } };
const densityConfig = { uom: 'g/cm³', format: { nearest: 0.1 } };
const temperatureConfig = { uom: '°C', format: { nearest: 0.1 } };
const lengthConfig = { uom: 'mm', format: { nearest: 0.1 } };

// Imported Sieve Analysis results, based on BS EN ISO 17892-4 Clause 5.2 Sieve Analysis
const sieveAnalysisPercentPassingConfig = { labelUom: '%', format: { nearest: 0.1 } };

const getConfig = (prefix: string) => {
  let config = {
    [`${prefix}.ImagesAndRemarks.Remarks`]: { maxLength: 255 },
    [`${prefix}.ImagesAndRemarks.InternalRemarks`]: { maxLength: 255 },
    [`${prefix}.HydrometerPortion.Preparation.ParticleDensity`]: { uom: 'Mg/m³', format: { nearest: 0.01 } },
    [`${prefix}.HydrometerPortion.Preparation.AssociatedParticleDensityTest.ExternalTest`]: {
      testMethods: [
        externalTestMethods.particleDensity.BS_EN_ISO_17892_3,
        externalTestMethods.particleDensity.ISO_17892_3,
      ],
    },
    [`${prefix}.HydrometerPortion.Preparation.AssociatedParticleDensityTest.ExternalTest.Results.ParticleDensity`]: {
      uom: 'Mg/m³',
      format: { nearest: 0.01 },
    },
    [`${prefix}.HydrometerPortion.Preparation.GrainSize`]: {
      options: [
        { label: 'Fine', value: GrainSize.Fine },
        { label: 'Coarse', value: GrainSize.Coarse },
      ],
    },
    ...getConfigForImportedTest(
      qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014,
      `${prefix}.HydrometerPortion.Preparation.AssociatedMoistureContentTest`
    ),
    [`${prefix}.HydrometerPortion.Preparation.AssociatedMoistureContentTest.ExternalTest`]: {
      testMethods: [externalTestMethods.moistureContent.BS_EN_ISO_17892_1],
    },
    ...getConfigForMeasuredTest(
      qestIds.moistureContent.BS_EN_ISO_17892_1.rev2014,
      `${prefix}.HydrometerPortion.Preparation.AssociatedMoistureContentTest`,
      { materialLabelOverride: 'Portion' }
    ),
    ...mcCOnfig(`${prefix}.HydrometerPortion.Preparation.AssociatedMoistureContentTest`),
    ...getMoistureContentPercent(
      `${prefix}.HydrometerPortion.Preparation.AssociatedMoistureContentTest.ExternalTest.Results.MoistureContentPercent`
    ),
    ...getConfigForImportedTest(
      qestIds.psdSieveAnalysis.BS_EN_ISO_17892_4_Clause_5_2.Rev2016,
      `${prefix}.Preparation.AssociatedSieveAnalysisTest`
    ),
    [`${prefix}.Preparation.Sieve_2_0`]: sieveAnalysisPercentPassingConfig,
    [`${prefix}.Preparation.Sieve_0_063`]: sieveAnalysisPercentPassingConfig,
    [`${prefix}.Preparation.TestStandardAndMethod`]: {
      options: [
        { label: 'BS 1377-2 Clause 10', value: 'Bs1377_2_Clause10_2022' },
        { label: 'BS EN ISO 17892-4 Clause 5.3', value: 'BsEnIso17892_4_Clause5_3_2016' },
      ],
    },

    //...getMoistureContentPercent(`${prefix}.HydrometerPortion.Masses.MoistureContent`),
    ...getPortionMassesConfig(`${prefix}.HydrometerPortion.Masses`, 'Portion'),
    ...getPortionMassMeasurementConfig(`${prefix}.HydrometerPortion.Masses.MassMeasurement`),
    ...getPortionPretreatmentConfig(`${prefix}.HydrometerPortion`),
    ...getPortionWashingConfig(`${prefix}.HydrometerPortion.Washing`),
    ...getPortionHydrometerMeasurementsConfig(`${prefix}.HydrometerPortion.Measurements`),
    [`${prefix}.HydrometerPortion.Agitation.DispersingAgentVolume`]: {
      uom: 'mL',
      format: { nearest: 1 },
    },
    [`${prefix}.HydrometerPortion.Agitation.Duration`]: {
      uom: 'h',
      format: { nearest: 0.01 },
    },

    [`${prefix}.Results.D85`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D60`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D50`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D30`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D15`]: { format: { nearest: 0.0001 } },
    [`${prefix}.Results.D10`]: { format: { nearest: 0.0001 } },
  };
  return mergeConfig(config, getLabelConfig(prefix));
};

const getPortionMassesConfig = (prefix: string, materialLabel: string) => ({
  [`${prefix}.WetMass`]: massConfig,
  [`${prefix}.DryMass`]: massConfig,
  ...getMoistureContentPercent(`${prefix}.MoistureContent`),
});

const getPortionMassMeasurementConfig = (prefix: string) =>
  mergeConfig(
    getMeasuredUsingConfigForPortionMassMeasurement(prefix),
    getDerivedFromMeasurementsConfigForPortionMassMeasurement(prefix),
    {
      [`${prefix}.TareID`]: { maxLength: 20 },
      [`${prefix}.Equipment.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
      [`${prefix}.Equipment.DryMassBalance`]: { defaultReadability: 0.01, defaultUom: 'g' },
    }
  );

const getPortionPretreatmentConfig = (portionPrefix: string) => {
  const prefix = `${portionPrefix}.Pretreatment`;
  const massMeasurementPrefix = `${portionPrefix}.Masses.MassMeasurement`;
  return mergeConfig({
    [`${prefix}.UntreatedMaterialDryMass`]: derivedFrom([
      `${massMeasurementPrefix}.TareMass`,
      `${massMeasurementPrefix}.TareAndWetMass`,
    ]),
    [`${prefix}.Equipment.Balance`]: { defaultReadability: 0.01, defaultUom: 'g' },
    [`${prefix}.TareID`]: { maxLength: 20 },
    [`${prefix}.TareMass`]: measuredUsing(`${prefix}.Equipment.Balance`),
    [`${prefix}.TareAndPretreatedMaterialDryMass`]: measuredUsing(`${prefix}.Equipment.Balance`),
    [`${prefix}.PretreatedMaterialDryMass`]: derivedFrom([
      `${prefix}.TareMass`,
      `${prefix}.TareAndPretreatedMaterialDryMass`,
    ]),
  });
};

const getPortionWashingConfig = (prefix: string) =>
  mergeConfig({
    [`${prefix}.WashedDryMass`]: massConfig,
    [`${prefix}.WashingsDryMass`]: massConfig,
    ...getConfigForMeasuredTest(qestIds.wash.BS_EN_ISO_17892_4_Clause_5_3.Rev2016, `${prefix}.AssociatedWashTest`),
  });

const getPortionHydrometerMeasurementsConfig = (prefix: string) =>
  mergeConfig({
    [`${prefix}.DateTimeTestStartedLocal`]: { format: { type: FormatTypes.DateTime } },

    [`${prefix}.Readings.DateTimeReadingLocal`]: {
      format: { type: FormatTypes.DateTime },
    },
    [`${prefix}.Readings.RawReading`]: densityConfig,
    [`${prefix}.Readings.ElapsedTimeInMinutes`]: { format: { nearest: 0.1 } },
    [`${prefix}.Readings.TrueReading`]: densityConfig,
    [`${prefix}.Readings.Temperature`]: temperatureConfig,
    [`${prefix}.Readings.EffectiveDepth`]: lengthConfig,
    [`${prefix}.Readings.ModifiedReading`]: densityConfig,
    [`${prefix}.Readings.ParticleDiameter`]: { uom: 'mm', format: { nearest: 0.0001 } },
    [`${prefix}.Readings.MassSmallerPercentUnscaled`]: { format: { nearest: 0.1 } },
    [`${prefix}.Readings.MassSmallerPercentScaled`]: { format: { nearest: 0.1 } },

    [`${prefix}.ReferenceReadings.RawReading`]: densityConfig,
    [`${prefix}.ReferenceReadings.TrueReading`]: densityConfig,
    [`${prefix}.ReferenceReadings.Temperature`]: temperatureConfig,

    [`${prefix}.HydrometerCalibrationMeniscusCorrection`]: densityConfig,
    [`${prefix}.HydrometerCalibrationIntercept`]: { format: { nearest: 0.01 } },
    [`${prefix}.HydrometerCalibrationSlope`]: { format: { nearest: 0.01 } },
  });

export { getConfig };

export default {
  [qestIds.hydrometer.BS_EN_ISO_17892_4_Clause_5_3.Rev2016]: getConfig,
};
